import type { RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type {
  OutputSummary,
  Preview,
  Proposal,
  StructuredIngestTable,
} from '@/services/api/structuredIngest'
import { SelectionDetail } from './Inspector'
import { MappingConversation, MappingProgress } from './MappingConversation'
import { MappingDrawing } from './MappingDrawing'
import type { LensView, StructuredIngestActions } from './StructuredIngestView'

interface WorkspaceData {
  table: StructuredIngestTable
  recipe: Proposal
  preview: Preview
  summary: OutputSummary
  schemaLabels: string[]
  lens: LensView | null
  busy: { reannotating: boolean; projecting: boolean; mappingLocked?: boolean }
}

interface WorkspaceSelection {
  id: string | null
  onSelect: (id: string | null) => void
  container: RefObject<HTMLDivElement | null>
}

export function MappingWorkspace({
  data,
  selection,
  actions,
}: {
  data: WorkspaceData
  selection: WorkspaceSelection
  actions: StructuredIngestActions
}) {
  return (
    <div
      ref={selection.container}
      className="grid overflow-hidden rounded-xl border bg-card lg:h-[clamp(28rem,calc(100vh-24rem),42rem)] lg:grid-cols-[minmax(0,1.35fr)_minmax(20rem,1fr)]"
    >
      <MappingEditor data={data} selection={selection} actions={actions} />
      <MappingConversation
        state={{
          table: data.table,
          busy: data.busy.reannotating,
          disabled: data.busy.reannotating || data.busy.projecting || !!data.busy.mappingLocked,
        }}
        onSend={message => actions.onReannotate(data.table.tableId, message)}
      />
    </div>
  )
}

function MappingEditor({
  data,
  selection,
  actions,
}: {
  data: WorkspaceData
  selection: WorkspaceSelection
  actions: StructuredIngestActions
}) {
  const { t } = useTranslation()
  const { table, busy } = data
  return (
    <section
      aria-label={t('tableMapping.workspace.region')}
      aria-busy={busy.reannotating}
      className="flex min-h-0 min-w-0 flex-col border-b lg:border-b-0 lg:border-r"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <h3 className="text-sm font-semibold">{t('tableMapping.workspace.title')}</h3>
        <Tabs
          value={data.lens ? 'row' : 'file'}
          onValueChange={mode =>
            actions.onLensChange(mode === 'row' ? table.tableId : null, data.lens?.position ?? 0)
          }
        >
          <TabsList className="h-7">
            <TabsTrigger value="file" className="text-xs" disabled={busy.reannotating}>
              {t('tableMapping.workspace.wholeFile')}
            </TabsTrigger>
            <TabsTrigger value="row" className="text-xs" disabled={busy.reannotating}>
              {t('tableMapping.workspace.rowByRow')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {busy.reannotating ? (
        <MappingProgress filename={table.facts.name} />
      ) : (
        <fieldset
          disabled={busy.projecting}
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto"
        >
          <MappingDrawing
            data={{ summary: data.summary, table, lens: data.lens }}
            selection={selection}
            onLensChange={actions.onLensChange}
          />
          {selection.id && (
            <MappingSelection data={data} selectedId={selection.id} actions={actions} />
          )}
          {!selection.id && (
            <p className="border-t px-4 py-3 text-xs text-muted-foreground">
              {t('tableMapping.workspace.hint')}
            </p>
          )}
        </fieldset>
      )}
    </section>
  )
}

function MappingSelection({
  data,
  selectedId,
  actions,
}: {
  data: WorkspaceData
  selectedId: string
  actions: StructuredIngestActions
}) {
  return (
    <div className="border-t p-4">
      <SelectionDetail
        data={{
          recipe: data.recipe,
          preview: data.preview,
          columns: data.table.columns,
          measureColumns: data.table.measureColumns,
          rowsTotal: data.table.facts.rows,
          schemaLabels: data.schemaLabels,
          selectedId,
          row: data.lens?.explanation ?? null,
        }}
        actions={{
          onChange: recipe => actions.onRecipeChange(data.table.tableId, recipe),
          onShowRows: (query, subtitle) =>
            actions.onShowRows(data.table.tableId, query, selectedId, subtitle),
        }}
      />
    </div>
  )
}
